import React from 'react';
import {observer} from 'mobx-react';
import {TechseeBaseComponent} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-component';
import {ITranslate} from '../../../../../services/LocalizationService';
import {CompareScanData, ScanType} from '../contract';
import {Download, Upload, SignalStrength, ConnectedDevices, ChannelNumber, OpenBox, CloseBox} from '../../../icons';
import {ArOutline} from '../../../icons';
import {wifiSearch, smallWifi} from '../../../../../components/connectivity-guru/wifi-analyzer-scan/icons';
import {ScanImageBox} from '../../scan-image-box/ScanImageBox';
import {RssiScaleBarOverlay} from '../../scan-image-box/RssiScaleBarOverlay';
import {ICompareController} from '../../../compare.controller';
import './ScanCompare.scss';

const formatDate = (date: string): string => date.replace(/(am|pm)/gi, (m) => m.toUpperCase());

export interface ScanCompareProps {
    scansGroups: CompareScanData[][];
    translate: ITranslate;
    controller: ICompareController;
}

@observer
export class ScanCompare extends TechseeBaseComponent<ScanCompareProps> {
    private renderWifiScanKpiCards(data: CompareScanData): JSX.Element {
        const kpiItems = [
            {icon: Download, key: 'downloadSpeed'},
            {icon: Upload, key: 'uploadSpeed'},
            {icon: SignalStrength, key: 'signalStrength'},
            {icon: ChannelNumber, key: 'channelNumber'},
            {icon: ConnectedDevices, key: 'connectedDevices'}
        ];

        return (
            <div className='kpi-cards-grid'>
                {kpiItems.map((item, k) => (
                    <div key={k} className='kpi-card'>
                        <div className='kpi-card-icon'>
                            <span className='icon'>{item.icon}</span>
                        </div>
                        <p className='kpi-card-value'>{data.info?.[item.key as keyof typeof data.info] ?? ''}</p>
                    </div>
                ))}
            </div>
        );
    }

    renderInternal(): JSX.Element {
        const {scansGroups, controller} = this.props;

        return (
            <div className='scan-compare'>
                {scansGroups.map((scans, i) => (
                    <div key={i} className='scan-compare-column'>
                        {scans.map((data, j) => {
                            const expanded = controller.isCardExpanded(i, j);

                            return (
                                <div key={j} className='scan-compare-header'>
                                    <div className='scan-card-row' onClick={() => controller.toggleCard(i, j)}>
                                        {data.type === ScanType.WIFI_SCAN ? (
                                            <div className='icon wifi-scan-icon'>{wifiSearch}</div>
                                        ) : (
                                            <div className='icon visual-scan-icon'>{ArOutline}</div>
                                        )}
                                        <p className='scan-type'>{data.type}</p>
                                        {data.type === ScanType.VISUAL_SCAN && data.floor && (
                                            <span className='scan-floor'>
                                                <span className='scan-floor-title'>| Floor</span>
                                                <span className='scan-floor-value'>{data.floor}</span>
                                            </span>
                                        )}
                                        <div className='toggle-card-icon'>{expanded ? OpenBox : CloseBox}</div>
                                    </div>

                                    {expanded ? (
                                        <div className='scan-compare-body'>
                                            {data.type === ScanType.WIFI_SCAN ? (
                                                this.renderWifiScanKpiCards(data)
                                            ) : (
                                                <ScanImageBox
                                                    imageUrl={data.imageUrl || ''}
                                                    className='scan-image-expanded'>
                                                    <RssiScaleBarOverlay />
                                                </ScanImageBox>
                                            )}
                                        </div>
                                    ) : (
                                        <div className='scan-compare-body'>
                                            {data.type === ScanType.WIFI_SCAN ? (
                                                <div className='wifi-scan-placeholder'>
                                                    {data.signalStrengthPercentage && (
                                                        <div
                                                            className='wifi-scan-placeholder-signal'
                                                            style={{color: data.signalStrengthPercentage.color}}>
                                                            <span className='wifi-icon'>{smallWifi}</span>
                                                            <span className='wifi-percentage'>
                                                                {data.signalStrengthPercentage.value}%
                                                            </span>
                                                        </div>
                                                    )}
                                                    {data.rssi != null && (
                                                        <p className='wifi-scan-placeholder-dbm'>{data.rssi}dBm</p>
                                                    )}
                                                </div>
                                            ) : (
                                                <ScanImageBox imageUrl={data.imageUrl || ''} className='scan-image' />
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        );
    }
}
